import { OptionsValues } from "components/types";
import { ActionMap } from "library/ActionMap";
import { AccountInfo } from "library/hooks/types";
import { yesNoOptions } from "library/sharedOptions";
import { MakeRequired } from "library/utils";
import { nzEmploymentStatusOptions } from "library/zendeskAPI/customFieldsMap/increaseCreditLimitCustomFieldsMap";
import { Dispatch } from "react";
import { HateoasLink } from "../types";
import {
  communicationMethodOptions,
  employmentStatusOptions,
  frequencyOptions,
  occupationOptions,
  relationshipStatusOptions,
  residentialStatusOptions,
} from "./options";

export enum PageType {
  LANDING = "LANDING",
  // AU
  NEW_CREDIT_LIMIT = "NEW_CREDIT_LIMIT",
  IMPORTANT_INFORMATION = "IMPORTANT_INFORMATION",
  ABOUT_YOURSELF = "ABOUT_YOURSELF",
  INCOME_AND_EXPENSES = "INCOME_AND_EXPENSES",
  REVIEW_DETAILS = "REVIEW_DETAILS",
  VERIFIER_TERMS = "VERIFIER_TERMS",
  FINAL_DECLINE = "FINAL_DECLINE",
  CONFIRM_MMP = "CONFIRM_MMP",
  ASSESSED = "ASSESSED",
  UNABLE_TO_INCREASE = "UNABLE_TO_INCREASE",
  OTP = "OTP",
  PROOF_OF_INCOME = "PROOF_OF_INCOME",
  APPROVED = "APPROVED",
  FINAL_CANCEL = "FINAL_CANCEL",
  // NZ
  NZ_FORM = "NZ_FORM",
  NZ_SUCCESS = "NZ_SUCCESS",
  // Common
  ERROR = "ERROR",
}

export enum ActionType {
  ALL_DATA_LOADED = "ALL_DATA_LOADED",
  ACCOUNT_INFO_LOADED_NZ = "ACCOUNT_INFO_LOADED_NZ",
  GET_APPLICATION_FAILED = "GET_APPLICATION_FAILED",
  GET_PERSONAL_DETAILS_FAILED = "GET_PERSONAL_DETAILS_FAILED",
  OTP_REQUIRED = "OTP_REQUIRED",
  NEW_CREDIT_LIMIT_IMPORTANT_INFORMATION_CLICKED = "NEW_CREDIT_LIMIT_IMPORTANT_INFORMATION_CLICKED",
  IMPORTANT_INFORMATION_BACK_CLICKED = "IMPORTANT_INFORMATION_BACK_CLICKED",
  NEW_CREDIT_LIMIT_SUBMITTED = "NEW_CREDIT_LIMIT_SUBMITTED",
  NEW_CREDIT_LIMIT_SUBMITTED_NZ = "NEW_CREDIT_LIMIT_SUBMITTED_NZ",
  ABOUT_YOURSELF_SUBMITTED = "ABOUT_YOURSELF_SUBMITTED",
  ABOUT_YOURSELF_BACK_CLICKED = "ABOUT_YOURSELF_BACK_CLICKED",
  INCOME_AND_EXPENSES_SUBMITTED = "INCOME_AND_EXPENSES_SUBMITTED",
  INCOME_AND_EXPENSES_BACK_CLICKED = "INCOME_AND_EXPENSES_BACK_CLICKED",
  REVIEW_DETAILS_ABOUT_YOU_UPDATE_DETAILS_CLICKED = "REVIEW_DETAILS_ABOUT_YOU_UPDATE_DETAILS_CLICKED",
  REVIEW_DETAILS_INCOME_AND_EXPENSES_UPDATE_DETAILS_CLICKED = "REVIEW_DETAILS_INCOME_AND_EXPENSES_UPDATE_DETAILS_CLICKED",
  REVIEW_DETAILS_VERIFIER_TERMS_CLICKED = "REVIEW_DETAILS_VERIFIER_TERMS_CLICKED",
  REVIEW_DETAILS_SUBMITTED = "REVIEW_DETAILS_SUBMITTED",
  REVIEW_DETAILS_BACK_CLICKED = "REVIEW_DETAILS_BACK_CLICKED",
  REVIEW_DETAILS_PENDING_POI = "REVIEW_DETAILS_PENDING_POI",
  REVIEW_DETAILS_CLI_PENDING_CUST_CALL = "REVIEW_DETAILS_CLI_PENDING_CUST_CALL",
  VERIFIER_TERMS_BACK_CLICKED = "VERIFIER_TERMS_BACK_CLICKED",
  FINAL_DECLINE = "FINAL_DECLINE",
  UNABLE_TO_INCREASE = "UNABLE_TO_INCREASE",
  READY_TO_CONFIRM_MMP = "READY_TO_CONFIRM_MMP",
  OTP_SESSION_CREATION_FAILED = "OTP_SESSION_CREATION_FAILED",
  OTP_SUBMITTED_SUCCESSFULLY = "OTP_SUBMITTED_SUCCESSFULLY",
  OTP_SUBMISSION_FAILED = "OTP_SUBMISSION_FAILED",
  OTP_RESEND_FAILED = "OTP_RESEND_FAILED",
  SUBMIT_APPLICATION_FAILED = "SUBMIT_APPLICATION_FAILED",
  ACKNOWLEDGE_APPLICATION_FAILED = "ACKNOWLEDGE_APPLICATION_FAILED",
  CONFIRM_MMP_SUCCEEDED = "CONFIRM_MMP_SUCCEEDED",
  CONFIRM_MMP_POI_REQUESTED = "CONFIRM_MMP_POI_REQUESTED",
  CLI_PENDING_ASSESSMENT = "CLI_PENDING_ASSESSMENT",
  CLI_PENDING_CUST_CALL = "CLI_PENDING_CUST_CALL",
  CLI_STA_PENDING_ACCEPTANCE = "CLI_STA_PENDING_ACCEPTANCE",
  CLI_PENDING_POI = "CLI_PENDING_POI",
  CLI_PENDING_ACCEPTANCE = "CLI_PENDING_ACCEPTANCE",
  CLI_NOT_ELIGIBLE = "CLI_NOT_ELIGIBLE",
  CLOSE_CANCEL_APPLICATION_MODAL = "CLOSE_CANCEL_APPLICATION_MODAL",
  OPEN_CANCEL_APPLICATION_MODAL = "OPEN_CANCEL_APPLICATION_MODAL",
  CLI_FINAL_CANCEL = "CLI_FINAL_CANCEL",
  FINAL_CANCEL = "FINAL_CANCEL",
  FINAL_CANCEL_FAILED = "FINAL_CANCEL_FAILED",
  CLI_CA_PENDING_ACCEPTANCE = "CLI_CA_PENDING_ACCEPTANCE",
}

type YesNoType = OptionsValues<typeof yesNoOptions>;

type RelationshipStatusType = OptionsValues<typeof relationshipStatusOptions>;

type ResidentialStatusType = OptionsValues<typeof residentialStatusOptions>;

type FrequencyType = OptionsValues<typeof frequencyOptions>;

export type FrequencyTypeUpperCase = Uppercase<FrequencyType>;

export type CommunicationMethod = OptionsValues<
  typeof communicationMethodOptions
>;

export type EmploymentStatusType = OptionsValues<
  typeof employmentStatusOptions
>;

type OccupationType = OptionsValues<typeof occupationOptions>;

type PersonalInformationType = {
  relationshipStatus: RelationshipStatusType;
  numberOfDependants: string;
};

type ResidentialAddressType = {
  residentialStatus: ResidentialStatusType;
  mortgageOrRent: string;
  timeAtCurrentAddress: {
    years: string;
    months: string;
  };
};

type EmploymentDetailsType = {
  employmentStatus: EmploymentStatusType;
  occupation: OccupationType;
  timeWithCurrentEmployer: {
    years: string;
    months: string;
  };
};

export type ExpensesType = {
  lifestyle: string;
  education: string;
  transport: string;
  insurances: string;
  utilities: string;
  personal: string;
};

type MonthlyExpensesType = {
  shareLivingExpenses: YesNoType;
  expenses: ExpensesType;
  totalExpenses: number | null;
};

type IncomeType = {
  netIncome: {
    amount: string;
    frequency: FrequencyType;
  };
  otherIncome: {
    amount: string;
    frequency: FrequencyType;
  };
  otherSourcesOfIncome: YesNoType;
};

type AdditionalDebtsType = {
  haveAdditionalDebts: YesNoType;
  loanRepayments: string;
  totalCreditLimit: string;
  totalOwing: string;
};

export type UserAddress = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  post_code: string;
  country: string;
};

export interface State {
  page: PageType;
  otpVerifyUrl?: string;
  otpResendUrl?: string;
  submitUrl?: string;
  acknowledgementUrl?: string;
  accountInfo?: AccountInfo;
  maskedMobileNumber?: string;
  userAddress?: UserAddress;
  userMobilePhoneNumber?: string;
  userHomePhoneNumber?: string;
  userWorkPhoneNumber?: string;
  contactInformationCorrect: YesNoType;
  newCreditLimit: string;
  requestedCreditLimit?: number;
  minimumMonthlyPayment?: number;
  personalInformation: PersonalInformationType;
  residentialAddress: ResidentialAddressType;
  employmentDetails: EmploymentDetailsType;
  income: IncomeType;
  isConditionallyApproved?: boolean;
  monthlyExpenses: MonthlyExpensesType;
  additionalDebts: AdditionalDebtsType;
  bankConnectUrl?: string;
  cancelUrl?: string;
  latitudePoiUrl?: string;
  expiryDate?: string;
  nzAdditionalIncome?: {
    amount: string;
    frequency: FrequencyType;
  };
  nzEmploymentStatus?: OptionsValues<typeof nzEmploymentStatusOptions>;
  nzAnyOtherSourcesOfIncome?: OptionsValues<typeof yesNoOptions>;
  nzIncomeChangesInFuture?: OptionsValues<typeof yesNoOptions>;
  nzAllExpensesForHousehold?: OptionsValues<typeof yesNoOptions>;
  nzHaveOffsetMortgage?: OptionsValues<typeof yesNoOptions>;
  nzPropertyRates?: OptionsValues<typeof yesNoOptions>;
  nzRevolvingMortgage?: string;
  nzHousingCosts?: OptionsValues<typeof yesNoOptions>;
  nzLivingCosts?: OptionsValues<typeof yesNoOptions>;
  nzFoodGroceriesEntertainment?: string;
  nzPersonalInsurance?: string;
  nzGeneralInsurance?: string;
  nzTransportAuto?: string;
  nzSecondaryMortgage?: {
    amount: string;
    frequency: FrequencyType;
  };
  nzEducationChildcare?: string;
  nzPersonalFitnessMedical?: string;
  nzAnyAdditionalExpense?: string;
  nzIncludeLumpSum?: OptionsValues<typeof yesNoOptions>;
  nzIntendLumpSum?: OptionsValues<typeof yesNoOptions>;
  nzOtherMonthlyLoanRepayments?: OptionsValues<typeof yesNoOptions>;
  nzLoanCommitmentsChanged?: OptionsValues<typeof yesNoOptions>;
  nzTotalExpenses?: string;
  nzTotalOwing?: string;
  nzOtherPropertyExpenses?: string;
  /*
    sessionExpiry is needed only when running with local mocks.
    It's used to simulate the session that the server creates.
    It allows us to return a different PersonalDetailsResponse based on whether
    a valid session exists or not.
  */
  sessionExpiry?: number;
}

type Payloads = {
  [ActionType.ALL_DATA_LOADED]: {
    accountInfo: AccountInfo;
    userAddress: UserAddress;
    userMobilePhoneNumber: string;
    userHomePhoneNumber?: string;
    userWorkPhoneNumber?: string;
    submitUrl: string;
    acknowledgementUrl: string;
  };
  [ActionType.ACCOUNT_INFO_LOADED_NZ]: {
    accountInfo: AccountInfo;
  };
  [ActionType.GET_APPLICATION_FAILED]: {
    accountInfo: AccountInfo;
  };
  [ActionType.GET_PERSONAL_DETAILS_FAILED]: {
    accountInfo: AccountInfo;
  };
  [ActionType.OTP_SESSION_CREATION_FAILED]: {
    accountInfo: AccountInfo;
  };
  [ActionType.OTP_REQUIRED]: {
    accountInfo: AccountInfo;
    maskedMobileNumber: string;
    otpVerifyUrl: string;
    otpResendUrl: string;
  };
  [ActionType.NEW_CREDIT_LIMIT_IMPORTANT_INFORMATION_CLICKED]: {
    contactInformationCorrect: YesNoType;
    newCreditLimit: string;
  };
  [ActionType.NEW_CREDIT_LIMIT_SUBMITTED]: {
    contactInformationCorrect: YesNoType;
    newCreditLimit: string;
    minimumMonthlyPayment: number;
  };
  [ActionType.ABOUT_YOURSELF_SUBMITTED]: {
    personalInformation: PersonalInformationType;
    residentialAddress: ResidentialAddressType;
    employmentDetails: EmploymentDetailsType;
  };
  [ActionType.ABOUT_YOURSELF_BACK_CLICKED]: {
    personalInformation: PersonalInformationType;
    residentialAddress: ResidentialAddressType;
    employmentDetails: EmploymentDetailsType;
  };
  [ActionType.INCOME_AND_EXPENSES_SUBMITTED]: {
    income: IncomeType;
    monthlyExpenses: MonthlyExpensesType;
    additionalDebts: AdditionalDebtsType;
  };
  [ActionType.INCOME_AND_EXPENSES_BACK_CLICKED]: {
    income: IncomeType;
    monthlyExpenses: MonthlyExpensesType;
    additionalDebts: AdditionalDebtsType;
  };
  [ActionType.REVIEW_DETAILS_PENDING_POI]: {
    bankConnectUrl: string;
    latitudePoiUrl: string;
  };
  [ActionType.READY_TO_CONFIRM_MMP]: {
    requestedCreditLimit: number;
    approvedCreditLimit: number;
    acknowledgementUrl: string;
    bankConnectUrl: string;
    expiryDate: string;
    cancelUrl: string;
    latitudePoiUrl: string;
    isConditionallyApproved?: boolean;
  };
  [ActionType.CONFIRM_MMP_SUCCEEDED]: {
    minimumMonthlyPayment: number;
    approvedCreditLimit: number;
  };
  [ActionType.CLI_PENDING_ASSESSMENT]: {
    accountInfo: AccountInfo;
  };
  [ActionType.CLI_PENDING_CUST_CALL]: {
    accountInfo: AccountInfo;
  };
  [ActionType.CLI_STA_PENDING_ACCEPTANCE]: {
    accountInfo: AccountInfo;
    requestedCreditLimit: number;
    approvedCreditLimit: number;
    isConditionallyApproved?: boolean;
    cancelUrl: string;
    acknowledgementUrl: string;
  };
  [ActionType.CLI_PENDING_POI]: {
    accountInfo: AccountInfo;
    bankConnectUrl: string;
    expiryDate: string;
    latitudePoiUrl: string;
    employmentStatus: EmploymentStatusType;
    cancelUrl: string;
  };
  [ActionType.CLI_PENDING_ACCEPTANCE]: {
    accountInfo: AccountInfo;
  };
  [ActionType.CLI_NOT_ELIGIBLE]: {
    accountInfo: AccountInfo;
  };
  [ActionType.CLI_FINAL_CANCEL]: {
    accountInfo: AccountInfo;
  };
  [ActionType.CONFIRM_MMP_POI_REQUESTED]: {
    bankConnectUrl: string;
    latitudePoiUrl: string;
    cancelUrl: string;
  };
};

export type Action = ActionMap<ActionType, Payloads>;

export type ScreenState = MakeRequired<State, "accountInfo">;

export type ScreenProps = {
  state: ScreenState;
  dispatch: Dispatch<Action>;
};

export enum ApplicationStatus {
  CLI_PENDING_ACCEPTANCE = "CLI_PENDING_ACCEPTANCE",
  CLI_PENDING_ASSESSMENT = "CLI_PENDING_ASSESSMENT",
  CLI_FINAL_APPROVE = "CLI_FINAL_APPROVE",
  INTERNAL_SERVICE_ERROR = "INTERNAL_SERVICE_ERROR",
  CLI_PENDING_POI = "CLI_PENDING_POI",
  CLI_NOT_ELIGIBLE = "CLI_NOT_ELIGIBLE",
  CLI_ELIGIBLE = "CLI_ELIGIBLE",
  CLI_STA_PENDING_ACCEPTANCE = "CLI_STA_PENDING_ACCEPTANCE",
  CLI_CA_PENDING_ACCEPTANCE = "CLI_CA_PENDING_ACCEPTANCE",
  CLI_PENDING_CUST_CALL = "CLI_PENDING_CUST_CALL",
  CLI_IN_PROGRESS = "CLI_IN_PROGRESS",
  CLI_FINAL_DECLINE = "CLI_FINAL_DECLINE",
  CLI_ACCOUNT_STATUS = "CLI_ACCOUNT_STATUS",
  CLI_FINAL_CANCEL = "CLI_FINAL_CANCEL",
}

export type Application = {
  status: ApplicationStatus;
  approved_credit_limit: number;
  requested_credit_limit: number;
  minimum_monthly_payment?: number;
  employment_status: string;
  expiry_date: string;
};

export type GetApplicationResponse = {
  application: Application;
  links: HateoasLink[];
};

export type SubmitApplicationResponse = {
  application: Application;
  links: HateoasLink[];
};

export type AcknowledgeApplicationResponse = {
  application: Application;
  links: HateoasLink[];
};

export type PersonalDetailsResponse = {
  email: string;
  mobile_phone: string;
  home_phone?: string;
  work_phone?: string;
  residential: UserAddress;
};

export type PersonalDetailsErrorResponse = {
  links: HateoasLink[];
};

export type SessionResponse = {
  mobile_phone: string;
  links: HateoasLink[];
};

export type VerifyOTPResponse = {
  verification_outcome: "OTP_Mismatch" | "OTP_OK";
};

export type FinalCancelCliResponse = {
  application: {
    status: "CLI_FINAL_CANCEL";
  };
  links: HateoasLink[];
};

export type SubmitApplicationRequest = {
  privacy_policy_acknowledgement: boolean;
  application: {
    requested_credit_limit: number;
    personal_information: {
      relationship_status: RelationshipStatusType;
      number_of_dependants: number;
    };
    residential_address: {
      residential_status: ResidentialStatusType;
      mortgage_or_rent: number;
      time_at_current_address_year: number;
      time_at_current_address_month: number;
    };
    employment_details: {
      employment_status: EmploymentStatusType;
      occupation: OccupationType;
      time_with_current_employer_year: number;
      time_with_current_employer_month: number;
    };
    income: {
      net_income: number;
      net_income_frequency: FrequencyTypeUpperCase;
      other_income: number;
      other_income_frequency: FrequencyTypeUpperCase;
    };
    monthly_expenses: {
      living_expenses_shared: boolean;
      education_childcare: number;
      transport: number;
      insurances: number;
      utilities: number;
      personal_expenses: number;
      lifestyle: number;
    };
    other_cards_loans: {
      loan_repayments: number;
      total_credit_limit: number;
      total_amounts_owing: number;
    };
  };
};

export type ScreenComponentType = React.FC<ScreenProps>;
export type PageInfo = {
  [key: string]: {
    heading: string;
    screenComponent: ScreenComponentType;
  };
};
